import {defineStore} from 'pinia';
import {ref, computed} from 'vue';

import type {DiffDisplayLevel} from '../api/view-preferences.ts';
import {DIFF_DISPLAY_LEVELS} from '../api/view-preferences.ts';

export type DiffToggleOption = {
  key: DiffDisplayLevel;
  label: string;
  showAddedChanged: boolean;
  showRemoved: boolean;
};

export const DIFF_TOGGLE_OPTIONS: DiffToggleOption[] = [
  {key: DIFF_DISPLAY_LEVELS.FULL, label: 'Full Diff', showAddedChanged: true, showRemoved: true},
  {key: DIFF_DISPLAY_LEVELS.ADDED, label: 'Added or Changed', showAddedChanged: true, showRemoved: false},
  {key: DIFF_DISPLAY_LEVELS.REMOVED, label: 'Removed', showAddedChanged: false, showRemoved: true},
  {key: DIFF_DISPLAY_LEVELS.NO, label: 'No Diff', showAddedChanged: false, showRemoved: false},
];

const DIFF_TOGGLE_OPTIONS_MAP: Record<DiffDisplayLevel, DiffToggleOption> = Object.fromEntries(
  DIFF_TOGGLE_OPTIONS.map((opt) => [opt.key, opt]),
) as Record<DiffDisplayLevel, DiffToggleOption>;

export const useDiffToggleStore = defineStore('diffToggle', () => {
  const selectedKey = ref<DiffDisplayLevel>('full');
  const enabled = ref(false);

  const selectedOption = computed(() => DIFF_TOGGLE_OPTIONS_MAP[selectedKey.value]);
  const showAddedChanged = computed(() => selectedOption.value.showAddedChanged);
  const showRemoved = computed(() => selectedOption.value.showRemoved);
  const isDiffVisible = computed(() => selectedKey.value !== 'no');

  function select(key: DiffDisplayLevel) {
    selectedKey.value = key;
  }

  function enable() {
    enabled.value = true;
  }

  function $reset() {
    selectedKey.value = DIFF_DISPLAY_LEVELS.FULL;
    enabled.value = false;
  }

  return {
    selectedKey,
    enabled,
    selectedOption,
    showAddedChanged,
    showRemoved,
    isDiffVisible,
    select,
    enable,
    $reset,
  };
});
